import type {
  YachtGameState,
  YachtViewerState,
  YachtViewerTurnState,
} from "../types";
import { selectLegalActions } from "./legal-actions";

function projectTurnSlot(slot: {
  readonly turnNumber: number;
  readonly sheetRound: number;
  readonly playerId: string;
  readonly dice: YachtViewerTurnState["dice"];
  readonly held: YachtViewerTurnState["held"];
  readonly rollCount: YachtViewerTurnState["rollCount"];
  readonly status: YachtViewerTurnState["status"];
}): YachtViewerTurnState {
  return {
    turnNumber: slot.turnNumber,
    sheetRound: slot.sheetRound,
    playerId: slot.playerId,
    dice: slot.dice,
    held: slot.held,
    rollCount: slot.rollCount,
    status: slot.status,
  };
}

export function selectViewerState(
  state: YachtGameState,
  viewerPlayerId?: string,
): YachtViewerState {
  const viewerId =
    viewerPlayerId && state.seatingOrder.includes(viewerPlayerId)
      ? viewerPlayerId
      : null;

  return {
    gameId: state.gameId,
    phase: state.phase,
    turnMode: state.turnMode,
    sheetRound: state.sheetRound,
    turnNumber: state.turnNumber,
    players: state.players,
    seatingOrder: state.seatingOrder,
    turnSlotsByPlayerId: Object.fromEntries(
      Object.entries(state.turnSlotsByPlayerId).map(([playerId, slot]) => [
        playerId,
        projectTurnSlot(slot),
      ]),
    ),
    scoreSheetsByPlayerId: state.scoreSheetsByPlayerId,
    legalActions: selectLegalActions(state, viewerId ?? undefined),
    viewerPlayerId: viewerId,
    lastScoreResult: state.lastScoreResult,
    rankings: state.rankings,
  };
}

export function buildViewerStates(
  state: YachtGameState,
  observerUserIds: readonly string[] = [],
): Readonly<Record<string, YachtViewerState>> {
  const recipientIds = [...state.seatingOrder, ...observerUserIds].filter(
    (id, index, all) => id.length > 0 && all.indexOf(id) === index,
  );

  return Object.fromEntries(
    recipientIds.map((recipientId) => [
      recipientId,
      selectViewerState(state, recipientId),
    ]),
  );
}
